import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import UnifiedNavigation from '../../components/Navigation/UnifiedNavigation';
import Footer from '../../components/Footer/Footer';
import InstagramSection from '../../components/BrandHomePage/InstagramSection';
import ContactUsSantaCatarina from '../../components/ContactUs/ContactUsSantaCatarina';
import { images } from '../../components/shared/assets';
import { useScrollManager } from '../../hooks/useScrollManager';
import { useOverlayManager } from '../../contexts/OverlayContext';
import { initializeAllFeatures, initializeDragScroll } from '../../utils/interactiveFeatures';
import { FilterBar } from '../../components/shared/FilterBar';
import { createSelfbookHandler } from '../../utils/selfbook';
// import './MeetingsEventsSantaCatarina.css';

const MeetingsEventsSantaCatarina: React.FC = () => {
  const navigate = useNavigate();
  const { scrollState } = useScrollManager();
  const { setHoveredDestination } = useOverlayManager();
  const [selectedEventType, setSelectedEventType] = useState<string | undefined>(undefined);
  const handleBooking = createSelfbookHandler(navigate, '/santa-catarina/contact/');

  useEffect(() => {
    window.scrollTo(0, 0);
    initializeAllFeatures();
    // Clear any lingering hover state to ensure correct footer variant
    setHoveredDestination(null);

    // FilterBar component handles filter functionality
    // No manual event listeners needed
  }, []);

  return (
    <div className="meetings-events-santa-catarina">
      {/* Navigation - Santa Catarina variant (light) */}
      <UnifiedNavigation variant="light" destination="santa-catarina" />

      {/* Hero Section with Diptych - Full Screen 50/50 */}
      <section className="meetings-hero-diptych" data-scroll-section>
        <div className="meetings-hero-text-block">
          <p className="meetings-hero-description">
            In the remote corners of the American continent — where guardian mountains rise, eternal ice lingers, stars burn brighter, and the jungle whispers with wonder — Awasi Experiences was born to curate your journey.
          </p>
        </div>
        <div
          className="meetings-hero-image-block"
          style={{ backgroundImage: `url('/assets/meetings and gatherings header image.jpg')` }}
        />
      </section>

      {/* Event Spaces Gallery with Filters */}
      <section className="meetings-occasions-section" data-scroll-section>
        <div className="meetings-occasions-content">
          {/* Filter buttons */}
          <FilterBar
            className="meetings-filter-bar"
            dropdowns={[
              {
                id: 'destination',
                type: 'destination',
                level: 'santa-catarina',
                options: [{ id: 'santa-catarina', value: 'santa-catarina', label: 'AWASI SANTA CATARINA' }],
                selectedValue: 'santa-catarina',
                onSelect: (value: string) => console.log('Destination:', value)
              },
              {
                id: 'type',
                type: 'type',
                level: 'santa-catarina',
                options: [
                  { id: 'corporate', value: 'corporate', label: 'CORPORATE' },
                  { id: 'celebrations', value: 'celebrations', label: 'CELEBRATIONS' },
                  { id: 'weddings', value: 'weddings', label: 'WEDDINGS' },
                  { id: 'groups', value: 'groups', label: 'GROUPS' },
                  { id: 'incentive', value: 'incentive', label: 'INCENTIVE' }
                ],
                selectedValue: selectedEventType,
                onSelect: (value: string) => setSelectedEventType(value === selectedEventType ? undefined : value)
              }
            ]}
            alignment="left"
          />

          {/* Occasions Gallery - 3x2 Grid */}
          <div className="meetings-occasions-gallery">
            {/* Row 1 */}
            <div className="meetings-occasion-card">
              <div className="meetings-occasion-image">
                <img src="/assets/images/localhost/e148a3c9b1b41cdcfe55bca79640b870fcb427c8.png" alt="Beach Celebrations" />
              </div>
              <div className="meetings-occasion-info">
                <h3>Birthdays</h3>
                <p>Celebrate special moments on pristine beaches or beneath swaying palms. Our Santa Catarina lodge offers intimate celebrations paired with ocean views and the sound of waves.</p>
                <button onClick={handleBooking} className="meetings-contact-link">
                  CONTACT US
                  <span className="link-underline"></span>
                </button>
              </div>
            </div>

            <div className="meetings-occasion-card">
              <div className="meetings-occasion-image">
                <img src="/assets/images/localhost/3c040adef04baeda73725b5b017494444ef32dec.png" alt="Ocean Adventures" />
              </div>
              <div className="meetings-occasion-info">
                <h3>Group Trips</h3>
                <p>Explore secluded islands and hidden beaches with exclusive boat access. Our marine experts craft bespoke journeys through Santa Catarina's coastal treasures and marine sanctuaries.</p>
                <button onClick={handleBooking} className="meetings-contact-link">
                  CONTACT US
                  <span className="link-underline"></span>
                </button>
              </div>
            </div>

            <div className="meetings-occasion-card">
              <div className="meetings-occasion-image">
                <img src="/assets/images/localhost/157764183566073919c0adb5caa263ab6baf7a8e.png" alt="Beach Weddings" />
              </div>
              <div className="meetings-occasion-info">
                <h3>Weddings & Celebrations</h3>
                <p>Exchange vows on golden sands with the Atlantic as your witness. Our coastal retreat creates magical moments where Brazilian warmth meets luxury beachfront hospitality.</p>
                <button onClick={handleBooking} className="meetings-contact-link">
                  CONTACT US
                  <span className="link-underline"></span>
                </button>
              </div>
            </div>

            {/* Row 2 */}
            <div className="meetings-occasion-card">
              <div className="meetings-occasion-image placeholder">
                {/* Placeholder for future content */}
              </div>
              <div className="meetings-occasion-info">
                <h3>Corporate Beach Retreats</h3>
                <p>Inspire your team in the tropical paradise of Santa Catarina. Our exclusive beach lodge provides the perfect setting for strategic thinking, team building, and celebrating success by the sea.</p>
                <button onClick={handleBooking} className="meetings-contact-link">
                  CONTACT US
                  <span className="link-underline"></span>
                </button>
              </div>
            </div>

            <div className="meetings-occasion-card">
              <div className="meetings-occasion-image placeholder">
                {/* Placeholder for future content */}
              </div>
              <div className="meetings-occasion-info">
                <h3>Surfing & Wellness</h3>
                <p>Combine team building with surf lessons and beach yoga. Create lasting bonds over shared ocean experiences, from catching waves to sunset meditation sessions on the beach.</p>
                <button onClick={handleBooking} className="meetings-contact-link">
                  CONTACT US
                  <span className="link-underline"></span>
                </button>
              </div>
            </div>

            <div className="meetings-occasion-card">
              <div className="meetings-occasion-image placeholder">
                {/* Placeholder for future content */}
              </div>
              <div className="meetings-occasion-info">
                <h3>Seafood Gastronomic Events</h3>
                <p>Create extraordinary culinary moments with fresh seafood prepared by renowned chefs. Our Santa Catarina sanctuary offers unparalleled beachfront settings for intimate dinners and celebrations.</p>
                <button onClick={handleBooking} className="meetings-contact-link">
                  CONTACT US
                  <span className="link-underline"></span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Diptych Carousel Section */}
      <section className="meetings-diptych-carousel horizontal-carousel-section" data-scroll-section>
        <div className="meetings-diptych-carousel__container" id="draggable-carousel">
          {/* First Diptych - Text with Small Image + Large Image */}
          <div className="meetings-diptych">
            <div className="meetings-diptych__text-panel">
              <ul className="meetings-diptych__checklist">
                <li>Private island excursions and beach picnics</li>
                <li>Dolphin watching and marine wildlife encounters</li>
                <li>Surf lessons with professional instructors</li>
                <li>Sunset catamaran cruises along the coast</li>
              </ul>
              <div className="meetings-diptych__small-image">
                <img src="/assets/images/localhost/c33b334690d591338bf28c1e9153c04d11f8dba5.png" alt="Small detail" />
              </div>
            </div>
            <div className="meetings-diptych__image-panel">
              <img
                src="/assets/images/localhost/6200c3abfabed50d490407fe1f8df401c703ad98.png"
                alt="Santa Catarina coastline"
                className="meetings-diptych__image"
              />
            </div>
          </div>

          {/* Scattered Collage Section */}
          <div className="meetings-scattered-collage">
            <div className="meetings-scattered__image meetings-scattered__image--1">
              <img src="/assets/images/localhost/0a1dff6a9e6afa9f3a7b16c78deaffb435413058.png" alt="Beach lounge" />
            </div>
            <div className="meetings-scattered__image meetings-scattered__image--2">
              <img src="/assets/images/localhost/b4326814b9fb849f82a940e0796e511a7be7446b.png" alt="Ocean view room" />
            </div>
            <div className="meetings-scattered__image meetings-scattered__image--3">
              <img src="/assets/images/localhost/4f8b22055f18ee8c9dacf02b5b640a93cfb017f0.png" alt="Beach area" />
            </div>
          </div>

          {/* Single Full Image */}
          <div className="meetings-single-image">
            <img
              src="/assets/images/localhost/33d5b125b1c7c803c9ceee3a7ef98e88d3a63f38.png"
              alt="Pool area"
              className="meetings-single-image__img"
            />
          </div>

          {/* Triptych Section */}
          <div className="meetings-triptych">
            <div className="meetings-triptych__image">
              <img src="/assets/images/localhost/5226d5c176daa595f4db55d4de23caf126e503b3.png" alt="Ocean terrace" />
            </div>
            <div className="meetings-triptych__image">
              <img src="/assets/images/localhost/476a389e59073cbfded36ae33e3246a8855d3f77.png" alt="Beach detail" />
            </div>
            <div className="meetings-triptych__image">
              <img src="/assets/images/localhost/81debd7e0047e7bf4ae1a23d7db43d63c1b81226.png" alt="Outdoor space" />
            </div>
          </div>

          {/* Final Full Image */}
          <div className="meetings-final-image">
            <img
              src="/assets/images/localhost/d8dbaa08f904fa823929d0f428310920f12625c7.png"
              alt="Awasi Santa Catarina beach area"
              className="meetings-final-image__img"
            />
          </div>
        </div>
      </section>

      {/* Contact Us Section with Santa Catarina Background */}
      <section className="meetings-contact-us-section" data-scroll-section>
        <ContactUsSantaCatarina />
      </section>

      {/* Instagram Gallery */}
      <InstagramSection />

      {/* Footer - Santa Catarina variant */}
      <Footer variant="santa-catarina" />
    </div>
  );
};

export default MeetingsEventsSantaCatarina;